import {useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {Input} from '@shared_web/components/core/input';
import {SvgIcon} from '@shared_web/components/core/svg_icon';

import {translate} from '@mavismart_frontend/components/translate';
import {icon_search} from '@mavismart_frontend/icons/icon_search';
import {INPUT_THEME} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

interface InputSearchProps<T> {
  items: T[] | undefined;
  setFilteredItems: (items: T[]) => void;
  getLabel: (item: T) => string;
}

export const InputSearch = <T,>({items, setFilteredItems, getLabel}: InputSearchProps<T>) => {
  const account = useAccount();
  const locale = getUserLocale(account);

  const [filter, setFilter] = useState<string>('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset the filter when the items change
  useEffect(() => setFilter(''), [items]);

  useEffect(() => {
    if (!items) return;
    setFilteredItems(
      items.filter(item =>
        getLabel(item).toLocaleLowerCase(locale).includes(filter.toLocaleLowerCase(locale))
      )
    );
  }, [filter, items, getLabel, setFilteredItems, locale]);

  const filterHandleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  }, []);

  return (
    <Wrapper>
      <WrapperInput>
        <InputStyled
          value={filter}
          onChange={filterHandleChange}
          placeholder={translate('search', getUserLanguage(account))}
          themeOverrides={{input: INPUT_THEME}}
        />
        <IconWrapper>
          <SvgIcon icon={icon_search} color="transparent" />
        </IconWrapper>
      </WrapperInput>
    </Wrapper>
  );
};

InputSearch.displayName = 'InputSearch';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 60%;
  transform: translateY(-50%);
  pointer-events: none; // Prevents the icon from blocking input events
`;

const WrapperInput = styled.div`
  position: relative;
  width: 100%;

  label {
    font-weight: 400;
    padding-left: 0;   
  }
`;

const InputStyled = styled(Input)`
  width: 100%;
`;
